import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Phone, Mail, Globe, MapPin, MessageCircle, Send, ArrowRight, Loader2, CheckCircle2, Linkedin, Youtube } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=2400&q=85";

const OFFICES = [
  { city: "Lagos (Head Office)", name: "Marine Infrastructure Africa Ltd", region: "Lagos, Nigeria", zone: "West Africa", image: "https://images.unsplash.com/photo-1590247813693-5541d1c609fd?auto=format&fit=crop&w=800&q=80" },
  { city: "Accra Office", name: "", region: "Accra, Ghana", zone: "West Africa", image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=800&q=80" },
  { city: "Nairobi Office", name: "", region: "Nairobi, Kenya", zone: "East Africa", image: "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?auto=format&fit=crop&w=800&q=80" },
  { city: "Cape Town Office", name: "", region: "Cape Town, South Africa", zone: "Southern Africa", image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&q=80" },
];

const AFRICA_POINTS = [
  { x: 130, y: 120, label: "West Africa" },
  { x: 200, y: 100, label: "North Africa" },
  { x: 110, y: 180, label: "Central Africa" },
  { x: 210, y: 180, label: "East Africa" },
  { x: 150, y: 230, label: "Southern Africa" },
  { x: 100, y: 260, label: "Southern Africa" },
];

// Small africa map
const AfricaMapPins = ({ className }) => (
  <svg viewBox="0 0 320 340" className={className} aria-hidden="true">
    <path
      d="M160 20c-30 0-58 12-78 34-18 22-25 46-27 72-2 24 3 46 13 66 9 20 21 38 26 58 5 20 6 40 15 56 8 15 22 26 40 30 12 3 26 3 40 3 14 0 28 0 40-3 18-4 32-15 40-30 9-16 10-36 15-56 5-20 17-38 26-58 10-20 15-42 13-66-2-26-9-50-27-72-20-22-48-34-78-34z"
      fill="#E4E9F0"
      stroke="#0B2545"
      strokeWidth="1"
    />
    {AFRICA_POINTS.map((p, i) => (
      <g key={i}>
        <circle cx={p.x} cy={p.y} r="12" fill="#0B2545" opacity="0.12" />
        <path d={`M${p.x} ${p.y-11} a5 5 0 1 1 0 10 a5 5 0 1 1 0 -10 z M${p.x - 3} ${p.y - 3} l6 8 l-3 3 z`} fill="#0B2545" />
      </g>
    ))}
  </svg>
);

const AFRICA_OPS = [
  "Coastal Protection",
  "Floating Infrastructure",
  "Waterfront Development",
  "Marine Access Systems",
  "Artificial Lakes & Water Features",
];

export default function Contact() {
  const [params] = useSearchParams();
  const preSolution = params.get("solution");

  const [form, setForm] = useState({
    full_name: "",
    company: "",
    email: "",
    phone: "",
    service_interest: preSolution || "",
    project_location: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { if (preSolution) setForm((f) => ({ ...f, service_interest: preSolution })); }, [preSolution]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", form);
      toast.success(data.message);
      setSent(true);
      setForm({ full_name: "", company: "", email: "", phone: "", service_interest: "", project_location: "", budget: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[color:var(--mia-navy)] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Waterfront" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--mia-navy)] via-[color:var(--mia-navy)]/85 to-[color:var(--mia-navy)]/25" />
        </div>
        <div className="mia-container py-14 lg:py-12 lg:py-20 relative">
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            CONTACT <span className="text-[color:var(--mia-green)]">US</span>
          </h1>
          <div className="w-16 h-1 bg-[color:var(--mia-green)] my-7" />
          <p className="text-white/85 max-w-lg leading-relaxed">
            We are here to help you build smarter, safer and more sustainable waterfronts. Get in touch with our team today.
          </p>
        </div>
      </section>

      {/* Form + Contact info */}
      <section className="py-16 bg-white">
        <div className="mia-container grid lg:grid-cols-[1.4fr,1fr] gap-12">
          {/* FORM */}
          <div>
            <div className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-[0.14em] uppercase">Send Us A Message</div>
            <div className="w-10 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-8" />

            {sent ? (
              <div className="p-8 bg-emerald-50 border border-emerald-200 rounded-md flex items-start gap-4" data-testid="contact-success">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 shrink-0" />
                <div>
                  <div className="font-display font-bold text-[color:var(--mia-navy)]">Message received.</div>
                  <p className="text-[color:var(--mia-muted)] mt-2 text-sm">A member of our team will be in touch within 24 hours.</p>
                  <button onClick={() => setSent(false)} className="mia-btn mia-btn-outline-navy mt-4" data-testid="contact-reset">Send another message</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
                <input required placeholder="Full Name *" value={form.full_name} onChange={on("full_name")} className="px-4 py-3.5 bg-[color:var(--mia-grey)] rounded-md border border-transparent focus:outline-none focus:border-[color:var(--mia-navy)]" data-testid="contact-name" />
                <input placeholder="Company / Organization" value={form.company} onChange={on("company")} className="px-4 py-3.5 bg-[color:var(--mia-grey)] rounded-md border border-transparent focus:outline-none focus:border-[color:var(--mia-navy)]" data-testid="contact-company" />
                <input required type="email" placeholder="Email Address *" value={form.email} onChange={on("email")} className="px-4 py-3.5 bg-[color:var(--mia-grey)] rounded-md border border-transparent focus:outline-none focus:border-[color:var(--mia-navy)]" data-testid="contact-email" />
                <input required placeholder="Phone Number *" value={form.phone} onChange={on("phone")} className="px-4 py-3.5 bg-[color:var(--mia-grey)] rounded-md border border-transparent focus:outline-none focus:border-[color:var(--mia-navy)]" data-testid="contact-phone" />
                <select required value={form.service_interest} onChange={on("service_interest")} className="px-4 py-3.5 bg-[color:var(--mia-grey)] rounded-md border border-transparent focus:outline-none focus:border-[color:var(--mia-navy)] sm:col-span-2 text-[color:var(--mia-navy)]" data-testid="contact-service">
                  <option value="">Project Type / Service of Interest *</option>
                  <option>Coastal Protection</option>
                  <option>Floating Infrastructure</option>
                  <option>Waterfront Development</option>
                  <option>Marine Access Systems</option>
                  <option>Artificial Lakes & Water Features</option>
                  <option>Other</option>
                </select>
                <input placeholder="Project Location" value={form.project_location} onChange={on("project_location")} className="px-4 py-3.5 bg-[color:var(--mia-grey)] rounded-md border border-transparent focus:outline-none focus:border-[color:var(--mia-navy)]" data-testid="contact-location" />
                <input placeholder="Estimated Budget (Optional)" value={form.budget} onChange={on("budget")} className="px-4 py-3.5 bg-[color:var(--mia-grey)] rounded-md border border-transparent focus:outline-none focus:border-[color:var(--mia-navy)]" data-testid="contact-budget" />
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[color:var(--mia-navy)] mb-2">Your Message / Project Details *</label>
                  <textarea required rows={5} placeholder="Tell us about your project, requirements and how we can help you." value={form.message} onChange={on("message")} className="w-full px-4 py-3.5 bg-[color:var(--mia-grey)] rounded-md border border-transparent focus:outline-none focus:border-[color:var(--mia-navy)] resize-none" data-testid="contact-message" />
                </div>
                <button type="submit" disabled={submitting} className="mia-btn mia-btn-navy sm:col-span-2" data-testid="contact-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send Message <Send className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>

          {/* GET IN TOUCH */}
          <div>
            <div className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-[0.14em] uppercase">Get In Touch</div>
            <div className="w-10 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-8" />

            <div className="space-y-6">
              {[
                { icon: Phone, label: "Phone", value: "+234 000 000 0000", extra: "Mon – Fri: 8:00 AM – 6:00 PM (WAT)" },
                { icon: Mail, label: "Email", value: "info@marineinfrastructure.africa", extra: "We respond within 24 hours." },
                { icon: Globe, label: "Website", value: "www.marineinfrastructure.africa" },
                { icon: MessageCircle, label: "WhatsApp", value: "+234 000 000 0000", extra: "Chat with our team instantly." },
                { icon: MapPin, label: "Head Office", value: "Marine Infrastructure Africa Ltd", extra: "Lagos, Nigeria · West Africa" },
              ].map((c) => (
                <div key={c.label} className="flex gap-4" data-testid={`contact-info-${c.label.toLowerCase()}`}>
                  <div className="w-11 h-11 rounded-full bg-[color:var(--mia-navy)] text-white flex items-center justify-center shrink-0"><c.icon className="w-5 h-5" strokeWidth={1.8} /></div>
                  <div>
                    <div className="text-[0.68rem] font-bold tracking-[0.16em] uppercase text-[color:var(--mia-muted)]">{c.label}</div>
                    <div className="font-semibold text-[color:var(--mia-navy)] mt-1 text-sm">{c.value}</div>
                    {c.extra && <div className="text-xs text-[color:var(--mia-muted)] mt-1">{c.extra}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OUR OFFICE LOCATIONS */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="mia-container">
          <h2 className="section-title text-2xl sm:text-3xl">OUR OFFICE LOCATIONS</h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OFFICES.map((o) => (
              <div key={o.city} className="bg-white rounded-md overflow-hidden border border-slate-100 card-lift" data-testid={`office-${o.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={o.image} alt={o.city} className="w-full h-full object-cover image-zoom" />
                </div>
                <div className="p-5">
                  <div className="font-display font-bold text-[color:var(--mia-navy)] text-sm tracking-[0.14em] uppercase leading-tight">{o.city}</div>
                  {o.name && <div className="text-sm text-[color:var(--mia-muted)] mt-3">{o.name}</div>}
                  <div className="text-sm text-[color:var(--mia-muted)]">{o.region}</div>
                  <div className="text-sm text-[color:var(--mia-muted)]">{o.zone}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPERATIONS + CONNECT */}
      <section className="py-16 bg-[color:var(--mia-grey)]">
        <div className="mia-container grid lg:grid-cols-2 gap-12">
          {/* Africa operations */}
          <div>
            <div className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-[0.14em] uppercase">Our Operations Across Africa</div>
            <div className="w-10 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-6" />
            <div className="grid grid-cols-[auto,1fr] gap-6 items-start">
              <AfricaMapPins className="w-52 h-52 lg:w-60 lg:h-60" />
              <div>
                <p className="text-sm text-[color:var(--mia-muted)] leading-relaxed">
                  We operate in multiple countries across Africa, delivering world-class marine and waterfront infrastructure solutions.
                </p>
                <ul className="mt-5 space-y-2">
                  {AFRICA_OPS.map((o) => (
                    <li key={o} className="flex items-start gap-2 text-sm text-[color:var(--mia-text)]">
                      <CheckCircle2 className="w-4 h-4 text-[color:var(--mia-green)] shrink-0 mt-0.5" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Connect with us */}
          <div>
            <div className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-[0.14em] uppercase">Connect With Us</div>
            <div className="w-10 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-8" />
            <div className="space-y-5">
              <a href="https://wa.me/2340000000000" target="_blank" rel="noopener noreferrer" className="flex gap-4 items-start" data-testid="connect-whatsapp">
                <div className="w-11 h-11 rounded-md bg-[color:var(--mia-green)] text-white flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold text-[color:var(--mia-navy)] text-sm tracking-[0.14em] uppercase">WhatsApp</div>
                  <div className="text-sm text-[color:var(--mia-muted)] mt-1">Chat with our team instantly.</div>
                  <div className="text-sm text-[color:var(--mia-green)] font-semibold mt-1">+234 000 000 0000</div>
                </div>
              </a>
              <div className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-md bg-[#0A66C2] text-white flex items-center justify-center shrink-0"><Linkedin className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold text-[color:var(--mia-navy)] text-sm tracking-[0.14em] uppercase">LinkedIn</div>
                  <div className="text-sm text-[color:var(--mia-muted)] mt-1">Follow us for updates and project insights.</div>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-md bg-[#FF0000] text-white flex items-center justify-center shrink-0"><Youtube className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold text-[color:var(--mia-navy)] text-sm tracking-[0.14em] uppercase">YouTube</div>
                  <div className="text-sm text-[color:var(--mia-muted)] mt-1">Watch our projects and company videos.</div>
                </div>
              </div>
              <div className="pt-3 flex flex-col gap-3">
                <Link to="/request-consultation" className="mia-btn mia-btn-primary w-full" data-testid="connect-cta-consultation">
                  Request Consultation <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <a href="tel:+2340000000000" className="mia-btn mia-btn-outline-navy w-full" data-testid="connect-cta-call">
                  <Phone className="w-3.5 h-3.5" /> Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 lg:py-12 bg-white">
        <div className="mia-container">
          <div className="bg-[color:var(--mia-navy)] rounded-md p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row gap-6 items-start lg:items-center text-white">
            <div className="w-14 h-14 rounded-full bg-[color:var(--mia-green)] text-white flex items-center justify-center shrink-0">
              <MessageCircle className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-xl sm:text-2xl leading-tight">Ready To Start Your Project?</div>
              <div className="text-sm text-white/70 mt-2">Let's work together to create sustainable, innovative and resilient waterfronts that stand the test of time.</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <Link to="/request-consultation" className="mia-btn mia-btn-white w-full sm:w-auto" data-testid="contact-cta-consultation">
                Request Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="mia-btn mia-btn-outline-white w-full sm:w-auto" data-testid="contact-cta-proposal">
                Get A Proposal <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
